/**
 * ARCADIA '84: REMASTERED — the cabinet catalog.
 *
 * Each entry fronts a standalone game app living in apps/games/<id>/,
 * served under /games/<id>/ (dev: vite proxy to the game's own server;
 * prod: the game's dist is copied into the shell's dist). The old
 * GameModule registry is gone — games own their loop, input, audio,
 * and rendering; the cabinet only launches, listens, and scores.
 *
 * `validatorVersion: 2` across the board — version 2 IS the remastered
 * generation in the `cartridges` DB registry (see V012 migration), so
 * remaster leaderboards never mix with '84 cartridge runs.
 */

export interface CatalogEntry {
  id: string;
  /** When the title joined the arcade (git date of its cartridge landing
   *  in the repo). Drives the gallery's "newest first" front row. */
  released: string;
  title: string;
  /** One-line card blurb. */
  blurb: string;
  genre: string;
  /** Neon accent used for the card's band, border glow, and title. */
  accent: string;
  /** Secondary accent for gradients. */
  accent2: string;
  /** The game's standalone dev-server port (vite proxy target in dev). */
  devPort: number;
  /** DB cartridge_version for score submission. 2 = remastered. */
  validatorVersion: number;
  /** Games with no fail state never submit scores (stray-pixel). */
  scoreless?: boolean;
}

export const REMASTER_VERSION = 2;

export const CATALOG: readonly CatalogEntry[] = [
  {
    id: 'neon-overpass',
    released: '2026-05-12',
    title: 'NEON OVERPASS',
    blurb: 'Full-3D circuit racing on an elevated highway over the bay.',
    genre: 'RACING',
    accent: '#ff3d9a',
    accent2: '#7a3cff',
    devPort: 5184,
    validatorVersion: REMASTER_VERSION,
  },
  {
    id: 'astrofall',
    released: '2026-05-13',
    title: 'ASTROFALL',
    blurb: 'Wireframe rocks, four shells, and a hyperspace you must earn.',
    genre: 'SHOOTER',
    accent: '#29e6ff',
    accent2: '#7a3cff',
    devPort: 5186,
    validatorVersion: REMASTER_VERSION,
  },
  {
    id: 'cargo-climb',
    released: '2026-05-14',
    title: 'CARGO CLIMB',
    blurb: 'Ride the crates up the construction site. Mind your head.',
    genre: 'PLATFORM',
    accent: '#ffd23d',
    accent2: '#ff8a3c',
    devPort: 5198,
    validatorVersion: REMASTER_VERSION,
  },
  {
    id: 'jade-tunnel',
    released: '2026-05-18',
    title: 'JADE TUNNEL',
    blurb: 'Race the sinking sun through the jungle to the idol vault.',
    genre: 'ADVENTURE',
    accent: '#27e060',
    accent2: '#ffd23d',
    devPort: 5204,
    validatorVersion: REMASTER_VERSION,
  },
  {
    id: 'cube-jump',
    released: '2026-05-26',
    title: 'CUBE JUMP',
    blurb: 'Paint the pyramid. Dodge the springers. Do not look down.',
    genre: 'HOPPER',
    accent: '#ffd23d',
    accent2: '#7a3cff',
    devPort: 5210,
    validatorVersion: REMASTER_VERSION,
  },
  {
    id: 'defense-grid',
    released: '2026-05-27',
    title: 'DEFENSE GRID',
    blurb: 'Six cities, three silos, ten waves of magenta rain.',
    genre: 'DEFENSE',
    accent: '#ff3d9a',
    accent2: '#29e6ff',
    devPort: 5212,
    validatorVersion: REMASTER_VERSION,
  },
  {
    id: 'bunker-84',
    released: '2026-05-31',
    title: 'BUNKER-84',
    blurb: 'First-person infiltration. Five sentinels between you and out.',
    genre: 'FPS',
    accent: '#ff5050',
    accent2: '#7a3cff',
    devPort: 5200,
    validatorVersion: REMASTER_VERSION,
  },
  {
    id: 'maze-patrol',
    released: '2026-05-14',
    title: 'MAZE PATROL',
    blurb: 'Tag the thieves before the maze itself rotates under you.',
    genre: 'MAZE',
    accent: '#ffd23d',
    accent2: '#29e6ff',
    devPort: 5196,
    validatorVersion: REMASTER_VERSION,
  },
  {
    id: 'trench-run',
    released: '2026-05-14',
    title: 'TRENCH RUN',
    blurb: 'Haul civilians out of the neon canyon. Payload slows you down.',
    genre: 'RESCUE',
    accent: '#29e6ff',
    accent2: '#ff3d9a',
    devPort: 5188,
    validatorVersion: REMASTER_VERSION,
  },
  {
    id: 'sector-seven',
    released: '2026-05-14',
    title: 'SECTOR SEVEN',
    blurb: 'Hold the rim of the tube. Dive when you dare — triple damage.',
    genre: 'TUBE',
    accent: '#7a3cff',
    accent2: '#29e6ff',
    devPort: 5194,
    validatorVersion: REMASTER_VERSION,
  },
  {
    id: 'interceptor',
    released: '2026-05-12',
    title: 'INTERCEPTOR',
    blurb: 'Night highway pursuit. The Switchblade lunges when you blink.',
    genre: 'PURSUIT',
    accent: '#29e6ff',
    accent2: '#ff5050',
    devPort: 5202,
    validatorVersion: REMASTER_VERSION,
  },
  {
    id: 'stardust',
    released: '2026-05-12',
    title: 'STARDUST',
    blurb: 'One ship, one life, and an ammo economy written in shards.',
    genre: 'SHOOTER',
    accent: '#7a3cff',
    accent2: '#ffd23d',
    devPort: 5192,
    validatorVersion: REMASTER_VERSION,
  },
  {
    id: 'city-hop',
    released: '2026-05-12',
    title: 'CITY HOP',
    blurb: 'Eight-way hops through traffic, trains, and the river fog.',
    genre: 'CROSSING',
    accent: '#27e060',
    accent2: '#29e6ff',
    devPort: 5190,
    validatorVersion: REMASTER_VERSION,
  },
  {
    id: 'pirate-fleet',
    released: '2026-05-12',
    title: 'PIRATE FLEET',
    blurb: 'Open-sea broadsides, buried gold, and a boss on the horizon.',
    genre: 'NAVAL',
    accent: '#ffd23d',
    accent2: '#ff3d9a',
    devPort: 5206,
    validatorVersion: REMASTER_VERSION,
  },
  {
    id: 'deep-core',
    released: '2026-07-10',
    title: 'DEEP CORE',
    blurb: 'Dig the neon strata, pump the blobs, respect the boulders.',
    genre: 'DIGGER',
    accent: '#ff00aa',
    accent2: '#ff8a3c',
    devPort: 5208,
    validatorVersion: REMASTER_VERSION,
  },
  {
    id: 'stray-pixel',
    released: '2026-07-11',
    title: 'STRAY PIXEL',
    blurb: 'The dot that never stopped bouncing. Chase the perfect corner.',
    genre: 'ZEN',
    accent: '#ffea00',
    accent2: '#29e6ff',
    devPort: 5214,
    validatorVersion: REMASTER_VERSION,
    scoreless: true,
  },
];

export function gameUrl(entry: CatalogEntry): string {
  // Explicit index.html, not the bare directory: CloudFront's S3 REST
  // origin has no directory-index support, so /games/<id>/ would miss,
  // 403, and get swallowed by the SPA fallback — serving the CABINET
  // inside the game iframe. The explicit key resolves everywhere: S3,
  // vite dev proxy, and vite preview.
  return `/games/${entry.id}/index.html`;
}
